/**
 * Scroll-linked marquee text
 * Duplicates the text to fill the viewport and translates it
 * horizontally based on page scroll position.
 * Uses lerp for smooth easing between scroll positions.
 * Targets the first .section-block-text on the page.
 */
export function scrollTextInit(): void {
  const section = document.querySelector<HTMLElement>('.section-block-text')
  if (!section) return

  const paragraph = section.querySelector<HTMLElement>('.section__body p')
  if (!paragraph) return

  // Build a marquee wrapper: duplicate text until it's wide enough to scroll seamlessly
  const text = paragraph.textContent?.trim() || ''
  if (!text) return

  const separator = ' ' // space between repeats
  const wrapper = document.createElement('span')
  wrapper.classList.add('scroll-text-track')

  // Start with one copy, measure, then duplicate until we have enough
  wrapper.textContent = text + separator
  paragraph.textContent = ''
  paragraph.appendChild(wrapper)

  // Duplicate until the track is at least 3x the section width
  const targetWidth = section.clientWidth * 3
  while (wrapper.scrollWidth < targetWidth) {
    wrapper.textContent += text + separator
  }

  const trackWidth = wrapper.scrollWidth
  const scrollSpeed = 0.15
  const easing = 0.06 // lower = smoother/slower easing (0.03–0.1 range)

  let currentX = 0
  let targetX = 0
  let animating = false

  const updateTarget = () => {
    const rect = section.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const progress = (viewportHeight - rect.top) / (viewportHeight + rect.height)
    const clampedProgress = Math.max(0, Math.min(1, progress))
    const maxTranslate = trackWidth - section.clientWidth
    const startOffset = -maxTranslate * scrollSpeed
    targetX = startOffset + clampedProgress * maxTranslate * scrollSpeed
  }

  const animate = () => {
    // Lerp toward target
    currentX += (targetX - currentX) * easing

    // Stop animating when close enough
    if (Math.abs(targetX - currentX) < 0.5) {
      currentX = targetX
      wrapper.style.transform = `translateX(${currentX}px)`
      animating = false
      return
    }

    wrapper.style.transform = `translateX(${currentX}px)`
    requestAnimationFrame(animate)
  }

  const onScroll = () => {
    updateTarget()

    if (!animating) {
      animating = true
      requestAnimationFrame(animate)
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true })

  // Initial position
  updateTarget()
  currentX = targetX
  wrapper.style.transform = `translateX(${currentX}px)`
}
